import moment from 'moment';
import _ from 'lodash';

import apiClient from '@shared/lib/general/apiClient';
import errorCatch from '@shared/lib/errorCatch';
import {
  CUSTOMER_DETAIL_CHANGE_CD_OPTIONS,
  CUSTOMER_DETAIL_CHANGE_VALUE,
  CUSTOMER_DETAIL_CHANGE_VALUES,
  CUSTOMER_DETAIL_CLEAR_FILTERED_DATA,
  CUSTOMER_DETAIL_EDIT_CONTACT_INFO,
  CUSTOMER_DETAIL_ERROR,
  CUSTOMER_DETAIL_FILTER_ACTIVITY_TAB,
  CUSTOMER_DETAIL_FILTER_ALERT_TAB,
  CUSTOMER_DETAIL_GENERATED_CSV,
  CUSTOMER_DETAIL_RESET_ALERT_TAB,
  CUSTOMER_DETAIL_RESET_EXPORT_RANGE,
  CUSTOMER_DETAIL_RESET_SELECT_POINT,
  CUSTOMER_DETAIL_RESET_STATE,
  CUSTOMER_DETAIL_RUNTIME_GRAPH,
  CUSTOMER_DETAIL_SELECT_POINT,
  CUSTOMER_DETAIL_SELECTED_IMAGE,
  CUSTOMER_DETAIL_SET_ACTIVITY,
  CUSTOMER_DETAIL_SET_ALERT_TAB,
  CUSTOMER_DETAIL_SET_CHRONIC_ALERTS,
  CUSTOMER_DETAIL_SET_CUSTOMER,
  CUSTOMER_DETAIL_SET_CYCLES,
  CUSTOMER_DETAIL_SET_EDIT,
  CUSTOMER_DETAIL_SET_EXPORT_RANGE,
  CUSTOMER_DETAIL_SET_FILTERED_DATA,
  CUSTOMER_DETAIL_RESET_MODAL,
  CUSTOMER_DETAIL_SET_SUBSCRIPTION,
  CUSTOMER_DETAIL_UPDATE_CHRONIC,
  CUSTOMER_DETAIL_UPLOAD_IMAGE,
  CUSTOMER_DETAIL_CANCEL_SUBSCRIPTION,
  CUSTOMER_DETAIL_CHANGE_CO_OPTIONS,
  DEVICE_DETAIL_CHANGE_VALUE,
  SERVICE_CHANGE_VALUE,
} from '../constants/mutationTypes';
import createCycles from './utility/createCycles';
import updateSystemFilter from './utility/updateSystemFilter';
import filterCycleData from './utility/filterCycleData';
import filterAlertsData from './utility/filterAlertsData';
import rangedData from './utility/rangedData';

const actions = {
  changeCdTableOption({ commit }, payload) {
    const {
      field,
      value,
    } = payload;

    commit({
      type: CUSTOMER_DETAIL_CHANGE_CD_OPTIONS,
      field,
      value,
    });
  },
  changeCoTableOption({ commit }, payload) {
    const {
      field,
      value,
    } = payload;

    commit({
      type: CUSTOMER_DETAIL_CHANGE_CO_OPTIONS,
      field,
      value,
    });
  },
  changeCustomerDetailValue({ commit }, payload) {
    const {
      field,
      value,
    } = payload;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field,
      value,
    });
  },
  async getWaterLeakAlerts({ commit }, customerId) {
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingWaterLeakList',
      value: true,
    });
    const category = 'Leak';
    const { data: waterAlertList } = await apiClient({
      method: 'GET',
      url: `/alerts/customer/${customerId}/category/${category}`,
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'loadingWaterLeakList',
        value: false,
      }, {
        field: 'waterLeakAlertList',
        value: waterAlertList,
      }],
    });
  },

  async getLatestRawPayload({ commit }, UID) {
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingRawPayload',
      value: true,
    });
    const UIDList = [UID];
    const { data: rawPayloads } = await apiClient({
      method: 'GET',
      url: `/devices/rawSensor/raw/uId/list/${UIDList}`,
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'loadingRawPayload',
        value: false,
      }, {
        field: 'rawPayloads',
        value: rawPayloads,
      }],
    });
  },
  async getCustomerPartnerSettings({ commit, state }) {
    const {
      customer: {
        corporationId,
      },
    } = state;
    const { data: partnerSettings } = await apiClient({
      method: 'GET',
      url: `/partner/settings/${Math.abs(corporationId)}`,
    });
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'customerPartnerSettings',
      value: partnerSettings,
    });
  },
  async getRecentCycles({ commit }, UID) {
    const startDate = moment().subtract(1, 'days').toISOString();
    const endDate = moment().toISOString();
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingCycleList',
      value: true,
    });
    const { data: cycleList } = await apiClient({
      method: 'GET',
      url: `/devices/acCycle/hub/${UID}/start/${startDate}/end/${endDate}`,
    });

    cycleList.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

    const lastTenCycleList = cycleList.length > 10 ? cycleList.slice(0, 10) : cycleList;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'loadingCycleList',
        value: false,
      }, {
        field: 'recentCycleList',
        value: lastTenCycleList,
      }],
    });
  },
  async getComfortCreditBalance({ commit }, customerId) {
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingComfortCredit',
      value: true,
    });
    const { data: balance } = await apiClient({
      method: 'GET',
      url: `/comfort/credits/balance/${customerId}`,
    });
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'loadingComfortCredit',
        value: false,
      }, {
        field: 'comfortCreditBalance',
        value: balance,
      }],
    });
    commit({
      type: SERVICE_CHANGE_VALUE,
      field: 'customerBalance',
      value: balance
    })
  },
  changeCustomerDetailValues({ commit }, payload) {
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: payload,
    });
  },
  resetCustomerDetailModal({ commit }) {
    commit({
      type: CUSTOMER_DETAIL_RESET_MODAL,
    });
  },
  selectedHvacImage({ commit }, payload) {
    const {
      image,
    } = payload;
    commit({
      type: CUSTOMER_DETAIL_SELECTED_IMAGE,
      image,
    });
  },
  async uploadHvacImage({ commit, state }, payload) {
    const {
      imageFormData,
    } = state;

    const imageReq = payload || imageFormData;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'uploadingImage',
      value: true,
    });

    const { data } = await apiClient({
      url: '/devices/ac/image/upload/new',
      method: 'POST',
      data: imageReq,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    commit({
      type: CUSTOMER_DETAIL_UPLOAD_IMAGE,
      response: data,
    });
  },
  async getNameplateRequestEvents({ commit, state }) {
    try {
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'gettingNameplateLogs',
        value: true,
      });

      const {
        currentHvac,
      } = state;
      const query = {
        uid: currentHvac.uid,
        alertType: 'Nameplate Requested',
      };

      const { data } = await apiClient({
        url: '/userevent/logs/query',
        method: 'POST',
        data: query,
      });

      if (data && data.length) {
        commit({
          type: CUSTOMER_DETAIL_CHANGE_VALUE,
          field: 'latestNameplateLog',
          value: data[0],
        });
      }
    } catch (err) {
      console.error(err);
    }

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'gettingNameplateLogs',
      value: false,
    });
  },
  async requestNameplatePhoto({ commit, state }) {
    try {
      const {
        currentHvac,
      } = state;

      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'requestingNameplate',
        value: true,
      });

      if (currentHvac.uid) {
        await apiClient({
          url: `/devices/ac/image/request/${currentHvac.uid}`,
          method: 'GET',
        });
      }
    } catch (err) {
      console.error(err);
    }
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'requestingNameplate',
      value: false,
    });
  },
  async vendorCatalogSearch({ commit, state }, payload = {}) {
    try {
      const {
        filters,
      } = state;

      const { search } = payload;

      const catalogIdList = filters.map((item) => item.catalogId);

      const { data } = await apiClient({
        method: 'POST',
        url: '/vendor/catalog/list',
        data: {
          catalogIds: catalogIdList,
          ...(search && {
            vendor: search,
          }),
        },
      });

      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'vendorCatalogs',
        value: data,
      });

      return data;
    } catch (err) {
      console.error(err);
    }
  },
  async cancelSubscription({
    commit, rootState, state, dispatch,
  }, cancelReason) {
    try {
      const {
        customer,
      } = state;

      const {
        admin,
      } = rootState;

      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'cancellingSubscription',
        value: true,
      });

      await apiClient({
        url: `/customers/id/${customer.id}/cancel`,
        method: 'PUT',
        data: {
          cancelReason,
        },
      });

      const newCustomer = {
        ...customer,
        accountStatus: 'Cancelled',
        cancelDate: moment().toISOString(),
        cancelReason: `${cancelReason} - ${admin?.current?.email}`,
      };

      dispatch('changeCustomerDetailValue', {
        field: 'customer',
        value: newCustomer,
      });

      commit({
        type: CUSTOMER_DETAIL_CANCEL_SUBSCRIPTION,
      });
    } catch (err) {
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'cancellingSubscription',
        value: false,
      });
      throw err;
    }
  },
  async getCommonFiltersByBrand({ commit }, payload) {
    try {
      const { filter } = payload;
      const { brand, thickness } = filter;

      const { data } = await apiClient({
        method: 'POST',
        url: '/filter/catalog/search',
        data: { brand },
      });

      const thicknessSet = new Set();

      data.forEach((item) => {
        thicknessSet.add(item.thickness);
      });

      if (thickness && !thicknessSet.has(thickness)) {
        thicknessSet.add(thickness);
      }
      const thicknessList = [...thicknessSet].sort().map((t) => ({ label: t, value: t }));

      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'commonFilters',
        value: data,
      });

      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'thicknessList',
        value: thicknessList,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
  async updateCustomerFilter({ commit, state }, payload) {
    const {
      filter,
    } = payload;

    const {
      filters,
    } = state;

    const {
      lastReplaced,
      catalogId,
      sbsCatalogId,
      sbsWidth,
      sbsHeight,
      sbsThickness,
      ...updateRequest
    } = filter;
    updateRequest.catalogId = Number(catalogId) || -1;
    updateRequest.sbsCatalogId = Number(sbsCatalogId) || -1;
    updateRequest.sbsWidth = Number(sbsWidth) || -1;
    updateRequest.sbsHeight = Number(sbsHeight) || -1;
    updateRequest.sbsThickness = Number(sbsThickness) || -1;

    const { data: updatedFilter } = await apiClient({
      method: 'PUT',
      url: `/devices/filter/update/id/${filter.id}`,
      data: updateRequest,
    });

    const updated = {
      ...updatedFilter,
      currentLife: updatedFilter.currentLifePercent,
    };

    const newUpdatedFilters = filters.map((item) => {
      const updatedItem = { ...item };
      if (updatedItem.filterId === updateRequest.filterId) {
        return {
          ...updatedItem,
          ...updated,
        };
      }
      return updatedItem;
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'filters',
      value: newUpdatedFilters,
    });
    return true;
  },
  async createCustomerFilter({ state }, payload) {
    const {
      filter,
    } = payload;

    const {
      customer,
    } = state;

    const filterRequest = {
      thermostatZoneId: filter.zoneId,
      lastReplaced: moment(new Date()).format('YYYY-MM-DD'),
      location: filter.location,
      ventLocation: filter.ventLocation,
      width: filter.width,
      height: filter.height,
      thickness: filter.thickness,
      merv: filter.merv,
      catalogId: Number(filter.catalogId) || -1,
      brand: filter.brand,
      customerId: customer.id,
      sbsHeight: Number(filter.sbsHeight) || -1,
      sbsWidth: Number(filter.sbsWidth) || -1,
      sbsThickness: Number(filter.sbsThickness) || -1,
      sbsCatalogId: Number(filter.sbsCatalogId) || -1,
      serviceAddressId: filter.serviceAddressId > 0 ? filter.serviceAddressId : null,
    };

    await apiClient({
      url: '/devices/filter/new',
      method: 'POST',
      data: filterRequest,
    });
    return true;
  },
  async replaceCustomerFilter({ commit, state }, payload) {
    const {
      filter,
    } = payload;

    const {
      filters,
    } = state;

    const lastReplacedDate = moment(filter.lastReplaced).format('YYYY-MM-DD');

    const updateRequest = {
      lastReplaced: moment(lastReplacedDate).format('YYYY-MM-DD'),
      height: filter.height,
      width: filter.width,
      thickness: filter.thickness,
      location: filter.location,
      ventLocation: filter.ventLocation,
      id: filter.id,
      filterId: filter.filterId,
      did: filter.did,
      uid: filter.uid,
      merv: filter.merv,
      size: filter.size,
      brand: filter.brand,
    };

    const { data } = await apiClient({
      url: `/devices/filter/replace/id/${filter.id}`,
      method: 'PUT',
      data: updateRequest,
    });

    const updatedFilters = filters.map((item) => {
      const updatedItem = { ...item };
      if (updatedItem.id === updateRequest.id) {
        return {
          ...updatedItem,
          ...data,
        };
      }
      return updatedItem;
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'filters',
      value: updatedFilters,
    });
    return true;
  },
  async deleteCustomerFilter({ commit, state }, payload) {
    const { id } = payload;
    const { filters } = state;

    await apiClient({
      url: `/customer/filter/delete/${id}`,
      method: 'DELETE',
    });

    const updatedFilters = filters.filter((filter) => filter.id !== id);

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'filters',
      value: updatedFilters,
    });
    return true;
  },
  async updateHub({ commit, state }, payload) {
    const {
      hub,
    } = payload;

    const {
      hubs,
    } = state;

    const updateRequest = {
      connectMethod: hub.connectMethod,
      uId: hub.uId,
      serviceAddressId: hub.serviceAddressId,
    };

    await apiClient({
      method: 'PUT',
      url: '/devices/hub/update',
      data: updateRequest,
    });

    const updatedHubs = hubs.map((item) => {
      const updatedItem = { ...item };
      if (updatedItem.uId === updateRequest.uId) {
        return {
          ...updatedItem,
          ...updateRequest,
        };
      }
      return updatedItem;
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'hubs',
      value: updatedHubs,
    });
    return true;
  },
  async updateSensor({ commit, state }, payload) {
    const {
      serialNumber,
      location,
      accessory,
      drainPan,
      acRelated,
      batteryUpdate,
      uid,
    } = payload;

    const {
      sensors,
      customer: {
        id,
      },
    } = state;
    await apiClient({
      method: 'PUT',
      url: `/devices/sensor/update/serial/${serialNumber}`,
      data: {
        serialNumber,
        location,
        accessory,
        drainPan,
        acRelated,
        batteryUpdate,
      },
    });

    await apiClient({
      method: 'POST',
      url: '/userevent/logs/new',
      data: {
        uid,
        customerId: id,
        serialNumber,
        logDate: batteryUpdate,
        eventCode: 'REPLACE_BATTERY',
      },
    });

    const updatedSensors = sensors.map((item) => {
      const updatedItem = { ...item };
      if (updatedItem.serialNumber === serialNumber) {
        return {
          ...updatedItem,
          ...{
            serialNumber,
            location,
            accessory,
            drainPan,
            acRelated,
            editOn: false,
            batteryUpdate,
          },
        };
      }
      return updatedItem;
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'sensors',
      value: updatedSensors,
    });
    return true;
  },
  async createServiceLog({ commit, state}, request) {
    try {
      await apiClient({
        method: 'POST',
        url: '/servicelogs/new',
        data: request,
      });

      const {
        hvac,
      } = state;

      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'hvac',
        value: hvac.map((h) => {
          if (h.uid === request.uid) {
            return {
              ...h,
              lastServiceDate: request.serviceDate,
            }
          }
          return h;
        }),
      })
    } catch (err) {
      console.error(err)
    }
  },
  async updatePropertyProfile({ commit, state}, request) {
    try {
      await apiClient({
        method: 'PUT',
        url: `/property/profile/update/${request.serviceAddressId}`,
        data: request,
      });

      const {
        propertyProfiles,
      } = state;

      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'propertyProfiles',
        value: propertyProfiles.map((pp) => {
          if (pp.serviceAddressId === request.serviceAddressId) {
            return {
              ...pp,
              ...request
            }
          }
          return pp;
        }),
      })
    } catch (err) {
      console.error(err)
    }
  },
  async updateCustomerProviderPlan({ dispatch, state }, payload) {
    const {
      customer,
      allCorpPlans,
    } = state;
    const {
      customerId,
      newProviderPlanId,
    } = payload;
    await apiClient({
      method: 'POST',
      url: `/customers/update/${customerId}/plan/${newProviderPlanId}`,
    });
    const providerPlan = allCorpPlans.find((plan) => plan.planId === newProviderPlanId);
    if (providerPlan?.planType) {
      const newCustomer = {
        ...customer,
        membershipType: providerPlan.planType === 'Hardware' ? 'Monitored' : providerPlan.planType,
      };
      dispatch('changeCustomerDetailValues', [{
        field: 'providerPlan',
        value: providerPlan,
      }, {
        field: 'customer',
        value: newCustomer,
      }]);
    }
  },
  async transferPartner({ dispatch, state }, payload) {
    const {
      activeTab,
    } = state;
    const {
      id,
      corporationId,
      providerPlanId,
    } = payload;

    await apiClient({
      method: 'PUT',
      url: `/customers/transfer/${id}`,
      data: {
        id,
        corporationId,
        providerPlanId,
      },
    });

    dispatch('setDetailCustomer', {
      customerId: id,
      activeTab,
    });
  },
  async setDetailCustomer({ dispatch, commit }, payload) {
    const {
      customerId,
      activeTab,
      actionType,
      wasDeleted,
    } = payload;

    if (!actionType) {
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'loading',
        value: true,
      });

      if (activeTab) {
        commit({
          type: CUSTOMER_DETAIL_CHANGE_VALUE,
          field: 'activeTab',
          value: activeTab,
        });
      }
    }

    const { data } = await apiClient({
      method: 'GET',
      url: `/customer/devices/noc/${customerId}`,
    });

    const { data: filters } = await apiClient({
      method: 'GET',
      url: `/customer/filter/all/${customerId}`,
    });

    const { customer, devices, propertyProfiles } = data;
    const hubs = [];
    const hvacs = [];
    const sensorList = [];
    const supplySensors = [];
    const systemFilterOptions = [];

    devices.forEach((d) => {
      const {
        deviceHub: hub,
        deviceAC: hvac,
        deviceSensors: sensors,
        deviceStatus: status,
        thermostatZones,
      } = d;
      if (!hub.status.toLowerCase().includes('deleted')) {
        if (status) {
          const hubStatus = status.find((stat) => stat.serialNumber === `${hub.uId}-0`);
          hub.status = hubStatus;
        } else {
          hub.status = null;
        }
        hubs.push(hub);
        const sList = [];
        if (sensors?.length > 0) {
          sensors.forEach((s) => {
            const {
              serialNumber,
            } = s;
            if (status) {
              const dStatus = status.find((stat) => stat.serialNumber === serialNumber);
              s.status = dStatus;
            } else {
              s.status = null;
            }
            sList.push(s);
          });
          sensorList.push(sList);
        }
        if (hvac) {
          if (hvac?.filters?.length) {
            hvac.filters.forEach((f) => {
              if (!filters.find(cf => cf.id === f.id)) {
                filters.unshift(f);
              }
            });
          }
          hvac.edit = false;

          if (thermostatZones?.length > 0) {
            hvac.thermostatZones = thermostatZones;
          }
          hvacs.push(hvac);
        }
      }
    });

    sensorList.forEach((sensor) => {
      sensor.forEach((s) => {
        if (s.sensorType === 'Temp') {
          supplySensors.push(
            {
              uId: s.uId,
              dId: s.deviceId,
              serial: s.serialNumber,
              location: s.location,
              systemName: hvacs.filter((h) => (s.uId === h.uId))[0]?.systemName,
            },
          );
        }
      });
    });

    const systemNames = [{ value: 'all', label: 'All' }];
    const tZones = [];
    let tZoneSerialNumber;
    hvacs.forEach((h, idx) => {
      const systemExist = systemNames.find((s) => s.value === h.uid);
      if (!systemExist) {
        const label = `${h.systemName} - ${h.uid.slice(-4)}`;
        systemNames.push({
          label,
          value: h.uid,
        });
      }

      if (h?.thermostatZones?.length > 0) {
        h.thermostatZones.forEach((tz) => {
          tZones.push(tz);
        });
        if (!tZoneSerialNumber) {
          tZoneSerialNumber = h.thermostatZones[0].serialNumber;
        }
      }

      systemFilterOptions.push(
        {
          uId: h.uId,
          manufacturer: h.manufacturer || idx,
          systemName: h.systemName,
          thermostatZones: h.thermostatZones,
        },
      );
    });

    let providerPlan = {};
    if (customer.providerPlanId) {
      const { data: plan } = await apiClient({
        method: 'GET',
        url: `/provider/plans/${customer.providerPlanId}`,
      });
      providerPlan = plan;
    }

    filters.forEach((f) => {
      let systemFound = hvacs.find((h) => h.uid === f.uid);
      if (!systemFound) {
        systemFound = hvacs.find((h) => h.serviceAddress?.id === f.serviceAddressId);
      }
      if (systemFound) {
        f.system = systemFound.systemName;
      }
    });
    commit({
      type: CUSTOMER_DETAIL_SET_CUSTOMER,
      customer,
      customerData: data,
      tZones,
      hubs,
      hvac: hvacs,
      sensors: sensorList.flat().filter((x) => !x.uId.includes('FF')),
      filters,
      systemNames,
      supplySensors,
      systemFilterOptions,
      providerPlan,
      propertyProfiles,
    });

    const uids = [];
    if (hvacs.length) {
      hvacs.forEach((a) => {
        if (a.uId) {
          uids.push(a.uId);
        }
      });
    }

    const chronicReq = [];
    uids.forEach((uid) => {
      chronicReq.push(apiClient({
        method: 'GET',
        url: `/alerts/chronic/${uid}`,
      }));
    });

    const caResponse = await Promise.all(chronicReq);
    const chronicAlerts = caResponse.flatMap((ca) => ca.data);

    commit({
      type: CUSTOMER_DETAIL_SET_CHRONIC_ALERTS,
      chronicAlerts,
    });

    const { data: subscriptions } = await apiClient({
      method: 'GET',
      url: `/customers/subscription/${customerId}`,
    });

    const allSubscriptions = {};
    subscriptions.forEach((s) => {
      const {
        description,
        quantity,
      } = s;
      if (allSubscriptions[description]) {
        allSubscriptions[description].quantity += quantity;
      } else {
        allSubscriptions[description] = s;
      }
    });

    commit({
      type: CUSTOMER_DETAIL_SET_SUBSCRIPTION,
      subscriptions: Object.values(allSubscriptions).map((sub) => sub),
    });
    if (wasDeleted) {
      dispatch('showSuccessToast', {
        header: 'Hub removed',
        message: `Successfully deactivated ${wasDeleted.uid}`,
      });
    }
  },
  async getCustomerAlert({ commit, rootState, state }, custId) {
    const {
      customer: {
        id,
      },
    } = state;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'getCustomerAlertLoading',
      value: true,
    });
    const {
      admin: {
        current: {
          corporationId,
        },
      },
    } = rootState;
    const { data: alerts } = await apiClient({
      method: 'GET',
      url: `/alerts/noc/customer/${custId ?? id}/all`,
    });

    const activeAlerts = [];
    const historyAlerts = [];
    alerts.forEach((a) => {
      if (a.archived) {
        historyAlerts.push(a);
      } else {
        activeAlerts.push(a);
      }
    });

    const customerAllAlerts = {};
    let alertTabCategories = [];
    if (corporationId === 1) {
      alertTabCategories = ['All', 'Performance', 'Device', 'Filter', 'Leak'];
    } else {
      alertTabCategories = ['All', 'Performance', 'Filter', 'Leak'];
    }

    customerAllAlerts.active = activeAlerts;
    customerAllAlerts.history = historyAlerts;
    commit({
      type: CUSTOMER_DETAIL_SET_ALERT_TAB,
      customerAllAlerts,
      tableData: activeAlerts,
      alertTabCategories,
      alertShowType: 'active',
    });
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'getCustomerAlertLoading',
      value: false,
    });
  },
  async getCustomerCycles({
    commit, dispatch, state,
  }) {
    const {
      tZones,
      customerData,
      systemFilter,
    } = state;
    const cycleRequest = [];
    const dailyRequest = [];
    const supplySensors = [];
    const supplySerials = [];
    customerData.devices.forEach((device) => {
      const idObj = device && device.deviceHub && device.deviceHub.uId && device.deviceHub.uId.split('_');
      const uid = idObj[0];
      if (uid && device?.deviceSensors) {
        device.deviceSensors.forEach((sensor) => {
          if (sensor.sensorType === 'Temp' || sensor.sensorType === 'A2TempHumidity') {
            supplySerials.push(sensor.serialNumber);
          }
        });
      }
    });

    const startDate = moment().subtract(7, 'days').toISOString();
    const actualEndDate = new Date().toISOString();
    tZones.forEach(({ serialNumber }) => {
      cycleRequest.push(
        apiClient({
          method: 'GET',
          url: `/devices/acCycle/hub/${serialNumber}/start/${startDate}/end/${actualEndDate}`,
        }),
      );
      dailyRequest.push(
        apiClient({
          method: 'GET',
          url: `/devices/acCycle/daily/hub/${serialNumber}/start/${startDate.split('T')[0]}/end/${actualEndDate.split('T')[0]}`,
        }),
      );
    });

    const result = await Promise.all(cycleRequest);
    const res = await Promise.all(dailyRequest);

    let runtimeGraphData;

    if (res && res[0] && res[0].data) {
      runtimeGraphData = res[0].data;
    }

    const cycles = createCycles({
      result,
      res,
      customer: customerData,
      sensors: supplySensors,
      tZones,
    });
    cycles.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

    const systemFilters = {
      ...systemFilter,
      singleTZone: tZones[0].serialNumber,
    };

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'supplySerials',
      value: supplySerials,
    });

    commit({
      type: CUSTOMER_DETAIL_SET_CYCLES,
      cycles,
    });

    commit({
      type: CUSTOMER_DETAIL_RUNTIME_GRAPH,
      graphData: runtimeGraphData,
      systemFilter: systemFilters,
    });

    dispatch('applySystemFilters');
  },
  async getCustomerThermostats({ commit, state }) {
    const {
      customer: {
        id,
      },
      tZones,
    } = state;
    const brands = [];
    tZones.forEach((el) => {
      if (!brands.find((x) => x === el.thermostatBrand)) {
        if (el.thermostatBrand !== null && el.thermostatBrand) {
          brands.push(el.thermostatBrand);
        }
      }
    });
    let list = [];
    brands.forEach(async (brand) => {
      const smartThermostatList = await apiClient({
        method: 'GET',
        url: `/thermostat/list/${brand}/${id}`,
      });
      list = [...list, ...smartThermostatList.data.thermostats];
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'smartThermostatList',
        value: list,
      });
    });
  },
  async getCustomerActivities({ state, commit }) {
    const {
      customer: {
        id,
      },
    } = state;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'activityLoading',
        value: true,
      }],
    });
    const activity = await apiClient({
      method: 'GET',
      url: `/customers/activity/${id}`,
    });
    commit({
      type: CUSTOMER_DETAIL_SET_ACTIVITY,
      activity: activity.data,
    });
  },
  async applySystemFilters({ commit, rootState }) {
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'cycleLoading',
      value: true,
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingRuntimeGraph',
      value: true,
    });

    try {
      const {
        admin: {
          current: {
            corporationId,
          },
        },
        customerDetail: {
          systemFilter,
          alertShowType,
          customerAllAlerts,
          allCycles,
          supplySerials,
          customerData,
          supplySensors,
          tZones,
          cdTableOptions,
        },
      } = rootState;

      let allCyclesData = allCycles.slice();
      let allCustomerAlerts;

      if (alertShowType === 'active') {
        allCustomerAlerts = customerAllAlerts.active.slice();
      } else {
        allCustomerAlerts = customerAllAlerts.history.slice();
      }

      const { newCycles, newDailyTrends } = await rangedData(
        systemFilter,
        supplySerials,
        corporationId,
        customerData,
        supplySensors,
        tZones,
      );
      allCyclesData = newCycles;

      const tableData = filterCycleData(allCyclesData, systemFilter);

      let alertsTableData = [];
      if (allCustomerAlerts && allCustomerAlerts.length) {
        alertsTableData = filterAlertsData(allCustomerAlerts, systemFilter);
      }

      const totalPages = Math.ceil(tableData.length / cdTableOptions.itemsPerPage);
      const newTableOptions = {
        ...cdTableOptions,
      };
      if (totalPages < newTableOptions.page) {
        newTableOptions.page = totalPages;
      }

      if (systemFilter.singleTZone) {
        commit({
          type: DEVICE_DETAIL_CHANGE_VALUE,
          key: 'selectedSupplySerial',
          value: systemFilter.singleTZone,
        });
      }

      commit({
        type: CUSTOMER_DETAIL_SET_FILTERED_DATA,
        tableData,
        alertsTableData,
        runtimeGraphData: newDailyTrends,
        systemFilter,
        cdTableOptions: newTableOptions,
      });
    } catch (err) {
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'cycleLoading',
        value: false,
      });
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'loadingRuntimeGraph',
        value: false,
      });
      throw err;
    }
  },
  clearAllSystemFilters({ commit, state }) {
    const {
      customerAllAlerts,
      allCycles,
      alertShowType,
      tZones,
    } = state;

    const systemFilter = {
      thermostatZone: [],
      cycleTypeFilter: [],
      deltaTFilter: [],
      dateFilter: 'past7',
      alertCategoryFilter: [],
      all: true,
      dateRange: {
        start: '',
        end: '',
      },
      singleTZone: tZones && tZones.length && tZones[0].serialNumber,
      allTZoneSelected: true,
    };

    let allAlerts;
    if (alertShowType === 'active') {
      allAlerts = customerAllAlerts.active.slice();
    } else {
      allAlerts = customerAllAlerts.history.slice();
    }

    if (systemFilter.singleTZone) {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'selectedSupplySerial',
        value: systemFilter.singleTZone,
      });
    }

    commit({
      type: CUSTOMER_DETAIL_CLEAR_FILTERED_DATA,
      systemFilter,
      allAlerts,
      allCycles,
    });
  },
  resetToRefFilter({ commit, state }) {
    const {
      systemFilterRef,
    } = state;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'systemFilter',
      value: systemFilterRef,
    });
  },
  setDetailSystemFilter({ commit, state }, payload) {
    const {
      systemFilter,
      systemFilterOptions,
    } = state;

    const {
      field,
      value,
      checked,
    } = payload;

    const newSystemFilter = updateSystemFilter(
      systemFilterOptions,
      systemFilter,
      field,
      value,
      checked,
    );

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'systemFilter',
      value: newSystemFilter,
    });
  },
  async setDetailCycleFilter({ commit, state }, payload) {
    const {
      cycleFilter,
      customerData,
      systemFilterOptions,
      tZones,
    } = state;
    const {
      field,
      value,
    } = payload;

    let newCycleFilter;

    if (field === 'range') {
      const startDate = moment(value.start);
      const threeMonthsBefore = moment().subtract(90, 'days');

      if (threeMonthsBefore.isAfter(startDate)) {
        const cycleRequest = [];
        customerData.devices.forEach((device) => {
          const idObj = device && device.deviceHub && device.deviceHub.uId && device.deviceHub.uId.split('_');
          const uid = idObj[0];
          const supplySerials = [];
          if (uid && device?.deviceSensors) {
            device.deviceSensors.forEach((sensor) => {
              if (sensor.sensorType === 'Temp') {
                supplySerials.push(sensor.serialNumber);
              }
            });
          }

          const endDate = moment(value.end).toISOString();
          tZones.forEach(({ serialNumber }) => {
            cycleRequest.push(
              apiClient({
                method: 'GET',
                url: `/devices/acCycle/hub/${serialNumber}/start/${startDate.toISOString()}/end/${endDate}`,
              }),
            );
          });
        });

        const result = await Promise.all(cycleRequest);

        const rangedCycles = createCycles({ result, customer: customerData });

        rangedCycles.sort((a, b) => {
          if (a.startTime > b.startTime) {
            return -1;
          }

          if (a.startTime < b.startTime) {
            return 1;
          }
          return 0;
        });

        commit({
          type: CUSTOMER_DETAIL_SET_CYCLES,
          rangedCycles,
        });
        newCycleFilter = {
          ...cycleFilter,
          date: '',
          [field]: value,
        };
      } else {
        newCycleFilter = {
          ...cycleFilter,
          date: '',
          [field]: value,
        };
      }
    } else if (field === 'date') {
      newCycleFilter = {
        ...cycleFilter,
        range: {
          start: null,
          end: null,
        },
        [field]: value,
      };
    } else if (field === 'deltaT') {
      const {
        field: f,
        value: v,
      } = value;
      newCycleFilter = {
        ...cycleFilter,
        deltaT: {
          five: false,
          ten: false,
          fifteen: false,
          twenty: false,
          twentyFive: false,
          thirty: false,
          [f]: v,
        },
      };
    } else if (field === 'uId') {
      const hvac = systemFilterOptions.find((sf) => sf.uId === value);
      if (hvac) {
        newCycleFilter = {
          ...cycleFilter,
          uId: value,
          tZoneSN: hvac.thermostatZones && hvac.thermostatZones.map((tz) => tz.serialNumber),
        };
      }
    }

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'cycleFilter',
      value: newCycleFilter,
    });
  },
  async getDailyStats({ commit }, payload) {
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingSummary',
      value: true,
    });
    const {
      UID,
      startDate,
    } = payload;

    const endDate = moment().format('YYYY-MM-DD');
    const { data: stats } = await apiClient({
      method: 'GET',
      url: `/devices/acCycle/daily/hub/${UID}/start/${startDate}/end/${endDate}`,
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingSummary',
      value: false,
    });
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'dailyStats',
      value: stats,
    });
  },
  setExportRange({ commit }, payload) {
    const {
      range,
      uid,
      start,
      end,
    } = payload;
    commit({
      type: CUSTOMER_DETAIL_SET_EXPORT_RANGE,
      range,
      uid,
      start,
      end,
    });
  },
  resetCustomerDetail({ commit }) {
    commit({
      type: CUSTOMER_DETAIL_RESET_STATE,
    });
  },
  async sendWelcomeEmail({ commit, dispatch }, payload) {
    const {
      email,
      isPortal,
    } = payload;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'sendingEmail',
      value: true,
    });
    try {
      await apiClient({
        method: 'POST',
        url: `/technicianac/email/welcome/send?isPortal=${isPortal}`,
        data: {
          toEmail: email,
        },
      });
      dispatch('showSuccessToast', {
        header: 'Welcome email sent!',
        message: `Email was sent to ${email}`,
      });
    } catch (err) {
      commit({
        type: CUSTOMER_DETAIL_ERROR,
        errorObj: {
          sendWelcomeEmailErr: 'There was an unexpected error.\nPlease contact support or try again.',
        },
      });
    }
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'sendingEmail',
      value: false,
    });
  },
  async getRedirectCode({ commit, dispatch }) {
    try {
      return await apiClient({
        method: 'GET',
        url: `/auth/get-redirect-passcode`,
      });
    } catch (err) {
      commit({
        type: CUSTOMER_DETAIL_ERROR,
        errorObj: {
          sendWelcomeEmailErr: 'There was an unexpected error.\nPlease contact support or try again.',
        },
      });
    }
  },
  async replaceSensor({ dispatch, state }, payload) {
    const {
      sensors,
    } = state;
    const {
      oldSerial,
      newSerial,
    } = payload;
    try {
      await apiClient({
        method: 'PUT',
        url: `/devices/sensor/replace?old=${oldSerial}&new=${newSerial}`,
      });
      dispatch('showSuccessToast', {
        message: `${oldSerial} sensor was successfully replaced by ${newSerial}`,
      });
      const newSensors = sensors.filter((s) => s.serialNumber !== oldSerial);
      dispatch('changeCustomerDetailValue', {
        field: 'sensors',
        value: newSensors,
      });
    } catch (err) {
      if (err.response.data) {
        dispatch('showAlertToast', {
          message: `There was an error replacing sensor ${err.response.data.message}`,
        });
      }
    }
  },
  setCustomerDetailEdit({ commit }, payload) {
    const {
      field,
    } = payload;
    commit({
      type: CUSTOMER_DETAIL_SET_EDIT,
      field,
    });
  },
  async updateContactInfo({ commit, state }) {
    const {
      newCustomerInfo: {
        id,
        firstName,
        lastName,
        email,
        phone,
        textNotification,
        emailNotification,
        hubspotId,
        techFirstName,
        techLastName,
      },
      newCustomerInfo,
    } = state;

    const errorObj = errorCatch('updateContactInfo', newCustomerInfo);

    commit({
      type: CUSTOMER_DETAIL_ERROR,
      errorObj,
    });

    if (Object.keys(errorObj).length === 0) {
      try {
        commit({
          type: CUSTOMER_DETAIL_CHANGE_VALUE,
          field: 'infoEditing',
          value: true,
        });

        const newCustomer = {
          id,
          firstName,
          lastName,
          email,
          phone,
          textNotification,
          emailNotification,
          hubspotId,
          techFirstName,
          techLastName,
        };

        await apiClient({
          method: 'PUT',
          url: `/customers/update/${id}`,
          data: newCustomer,
        });

        const nCustomer = {
          ...newCustomerInfo,
          ...newCustomer,
        };

        commit({
          type: CUSTOMER_DETAIL_EDIT_CONTACT_INFO,
          customer: nCustomer,
        });
      } catch (err) {
        if (err.response.data) {
          const {
            message,
          } = err.response.data;

          if (message.includes('EMAIL_UNIQUE')) {
            commit({
              type: CUSTOMER_DETAIL_ERROR,
              errorObj: {
                emailErr: 'Email is already in use.',
              },
            });
          }
        }
        commit({
          type: CUSTOMER_DETAIL_ERROR,
          errorObj: {
            editingErr: 'There was an unexpected error.\nPlease contact support or try again.',
          },
        });
        throw err;
      }
    }
  },
  async updateAddressByType({ commit, state }, payload) {
    const {
      id,
      customerId,
      address1,
      address2,
      type,
      city,
      state: addressState,
      latitude,
      longitude,
      zip,
      country,
    } = payload;
    const newAddress = {
      id,
      type,
      address1,
      address2,
      city,
      state: addressState,
      latitude,
      longitude,
      zip,
      country,
    };
    await apiClient({
      method: 'PUT',
      url: `/addresses/customers/${customerId}/${id}`,
      data: newAddress,
    });

    let nCustomer = { ...state.customer };
    if (type === 'SERVICE' || type === 'SHIPPING') {
      const addressType = type === 'SERVICE' ? 'serviceAddress' : 'shippingAddresses';
      const addresses = nCustomer[addressType]
        .map((ad) => (ad.id === newAddress.id ? { ...ad, ...newAddress } : ad));
      nCustomer = { ...nCustomer, [addressType]: addresses };
    } else if (type === 'BILLING') {
      nCustomer = { ...nCustomer, billingAddress: { ...nCustomer.billingAddress, ...newAddress } };
    }

    commit({
      type: CUSTOMER_DETAIL_EDIT_CONTACT_INFO,
      customer: nCustomer,
    });
  },
  async updateHVACDetail({ commit, state }, payload) {
    const {
      id,
      systemName,
    } = payload;

    const {
      hvac,
    } = state;

    await apiClient({
      url: `/devices/ac/update/id/${id}`,
      method: 'PUT',
      data: {
        id,
        systemName,
      },
    });

    const updatedHvacs = hvac.map((item) => {
      const updatedItem = { ...item };
      if (updatedItem.id === id) {
        return {
          ...updatedItem,
          ...{ id, systemName },
        };
      }
      return updatedItem;
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'hvac',
      value: updatedHvacs,
    });
    return true;
  },
  selectDataForExport({ commit }, payload) {
    const {
      data,
    } = payload;
    commit({
      type: CUSTOMER_DETAIL_SELECT_POINT,
      data,
    });
  },
  async exportRawDataCSV({ commit, state }) {
    const {
      exportCSV,
    } = state;

    try {
      let startDate;
      let endDate;
      let uid;
      if (exportCSV.range.start && exportCSV.range.end) {
        startDate = moment(exportCSV.range.start).toISOString();
        endDate = moment(exportCSV.range.end).toISOString();
      }
      if (exportCSV.uid) {
        const { uid: exportCsvUID } = exportCSV;
        uid = exportCsvUID;
      }

      const diff = moment(endDate).diff(moment(startDate), 'days');

      if (diff > 9) {
        commit({
          type: CUSTOMER_DETAIL_CHANGE_VALUE,
          field: 'showWrongDateRangePopup',
          value: true,
        });
      } else if (diff > 3) {
        commit({
          type: CUSTOMER_DETAIL_CHANGE_VALUE,
          field: 'generatingCSV',
          value: true,
        });

        const res = await apiClient({
          method: 'GET',
          url: `/export/payload/${uid}/${startDate}/${endDate}`,
          headers: {
            Accept: 'application/zip, text/csv',
          },
          responseType: 'blob',
        });

        const headerLine = res.headers['content-disposition'];
        const startFileNameIndex = headerLine.indexOf('"') + 1;
        const endFileNameIndex = headerLine.lastIndexOf('"');
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex);

        commit({
          type: CUSTOMER_DETAIL_GENERATED_CSV,
        });

        return {
          data: res.data,
          fileName,
        };
      } else {
        commit({
          type: CUSTOMER_DETAIL_CHANGE_VALUE,
          field: 'generatingCSV',
          value: true,
        });

        const res = await apiClient({
          method: 'GET',
          url: `/export/payload/${uid}/${startDate}/${endDate}/csv`,
          headers: {
            Accept: 'application/zip, text/csv',
          },
          responseType: 'blob',
        });
        const headerLine = res.headers['content-disposition'];
        const startFileNameIndex = headerLine.indexOf('"') + 1;
        const endFileNameIndex = headerLine.lastIndexOf('"');
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex);

        commit({
          type: CUSTOMER_DETAIL_GENERATED_CSV,
        });

        return {
          data: res.data,
          fileName,
        };
      }
      return false;
    } catch (err) {
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'generatingCSV',
        value: false,
      });
      throw err;
    }
  },
  setFilterAlertsTab({ commit }, payload) {
    const {
      filter,
      value,
    } = payload;

    commit({
      type: CUSTOMER_DETAIL_FILTER_ALERT_TAB,
      field: filter,
      value,
    });
  },
  async toggleAlertTypeType({ commit, state }, payload) {
    commit({
      type: CUSTOMER_DETAIL_RESET_ALERT_TAB,
    });

    const {
      value,
    } = payload;

    const {
      alertTabFilter: {
        zone,
        category,
      },
    } = state;

    const newCustomerAllAlerts = _.cloneDeep(state.customerAllAlerts);

    let newTableData = [];
    if (value === 'history') {
      newTableData = newCustomerAllAlerts.history;
    } else {
      newTableData = newCustomerAllAlerts.active;
    }

    if (zone && zone !== 'All') {
      newTableData = newTableData.filter((a) => a.zone === zone);
    }

    if (category && category !== 'All') {
      newTableData = newTableData.filter((a) => a.category === category);
    }

    commit({
      type: CUSTOMER_DETAIL_SET_ALERT_TAB,
      customerAllAlerts: newCustomerAllAlerts,
      tableData: newTableData,
      alertShowType: value,
    });
  },
  resetSelectedPoint({ commit }) {
    commit({
      type: CUSTOMER_DETAIL_RESET_SELECT_POINT,
    });
  },
  resetExportCSVRange({ commit }) {
    commit({
      type: CUSTOMER_DETAIL_RESET_EXPORT_RANGE,
    });
  },
  setFilterActivityTab({ commit }, payload) {
    const {
      filter,
      value,
    } = payload;

    commit({
      type: CUSTOMER_DETAIL_FILTER_ACTIVITY_TAB,
      field: filter,
      value,
    });
  },
  async removeChronicAlert({ commit }, payload) {
    try {
      await apiClient({
        url: '/alerts/chronic/delete',
        method: 'POST',
        data: payload,
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
    commit({
      type: CUSTOMER_DETAIL_UPDATE_CHRONIC,
      uid: payload.uid,
      alertType: payload.alertType,
    });
  },
  async setMergeMemberForm({ dispatch, state }) {
    const {
      selectedMergeCustomer: {
        customerId,
      },
      hubs,
      selectedMergeCustomer,
    } = state;

    const { data: serviceAddress } = await apiClient({
      method: 'GET',
      url: `/addresses/Service/customers/${customerId}`,
    });

    const mergeForm = hubs.map((hub) => {
      const {
        uId,
      } = hub;
      const [mergeToAddress] = serviceAddress;
      return {
        uId,
        serviceAddressId: mergeToAddress.id,
      };
    });

    dispatch('changeCustomerDetailValues', [{
      field: 'mergeForm',
      value: mergeForm,
    }, {
      field: 'actionModalView',
      value: 'merge-member-form',
    }, {
      field: 'selectedMergeCustomer',
      value: {
        ...selectedMergeCustomer,
        serviceAddress,
      },
    }]);
  },
  async mergeMember({ commit, state }) {
    const {
      customer,
      selectedMergeCustomer,
      mergeForm,
    } = state;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'mergingMember',
      value: true,
    });
    try {
      await apiClient({
        method: 'PUT',
        url: `/merge/member/from/${customer.id}/to/${selectedMergeCustomer.customerId}`,
        data: {
          fromId: customer.id,
          toId: selectedMergeCustomer.customerId,
          hubs: mergeForm,
        },
      });
    } catch (err) {
      console.error(err);
    }
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'mergingMember',
      value: false,
    });
  },
  async getPartnerProviderPlans({ commit, state, rootState }, payload) {
    const {
      customer: {
        corporationId,
      },
    } = state;
    const {
      admin: {
        adminFilter: {
          corpId,
        },
      },
    } = rootState;
    let id = corporationId || corpId;
    if (payload && payload.corpId) {
      id = payload.corpId;
    }
    const { data } = await apiClient({
      method: 'GET',
      url: `/provider/plans/corp/${id}`,
    });
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'allCorpPlans',
        value: data,
      }],
    });
  },
  async getAllCorpPlansWithArchived({ commit, state, rootState }, payload) {
    const {
      customer: {
        corporationId,
      },
    } = state;
    const {
      admin: {
        adminFilter: {
          corpId,
        },
      },
    } = rootState;
    let id = corporationId || corpId;
    if (payload && payload.corpId) {
      id = payload.corpId;
    }
    const { data } = await apiClient({
      method: 'GET',
      url: `/provider/plans/corp/${id}/all`,
    });
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'allCorpPlansWithArchived',
        value: data,
      }],
    });
  },
  async createNewServiceAddress({ dispatch, state }, payload) {
    const {
      address1,
      address2,
      city,
      state: aState,
      zip,
    } = payload;
    const {
      customer,
      hubs,
      currentHub,
    } = state;
    const newAddress = {
      customerId: customer.id,
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      phone: customer.phone,
      address1,
      address2,
      city,
      state: aState,
      zip,
      country: 'US',
      type: 'Service',
    };
    const { data: createdAddress } = await apiClient({
      method: 'POST',
      url: `/addresses/customers/${customer.id}`,
      data: newAddress,
    });

    const updateHub = {
      uId: currentHub.uId,
      serviceAddressId: createdAddress.id,
    };

    await apiClient({
      method: 'PUT',
      url: '/devices/hub/update',
      data: updateHub,
    });

    const newCustomer = _.cloneDeep(customer);

    newCustomer.serviceAddress.push(createdAddress);

    const newHubs = _.cloneDeep(hubs);
    newHubs.forEach((hub) => {
      if (hub.uId === currentHub.uId) {
        hub.serviceAddressId = createdAddress.id;
      }
    });

    dispatch('changeCustomerDetailValues', [{
      field: 'customer',
      value: newCustomer,
    }, {
      field: 'hubs',
      value: newHubs,
    }, {
      field: 'showActionModal',
      value: false,
    }]);
  },
  async getVirtualVisits({ dispatch, state }) {
    const {
      customer,
    } = state;
    const {
      id,
    } = customer;
    const { data } = await apiClient({
      method: 'GET',
      url: `/virtual/visit/get/videos/${id}`,
    });
    dispatch('changeCustomerDetailValues', [{
      field: 'virtualVisits',
      value: data,
    }]);
  },

  async deleteVirtualVisit({ dispatch, state }) {
    const {
      virtualVisits,
      selectedVirtualVisit,
    } = state;
    try {
      await apiClient({
        method: 'POST',
        url: '/virtual/visit/delete/video',
        data: selectedVirtualVisit,
      });
      const newVirtualVisits = virtualVisits.filter((v) => v.id !== selectedVirtualVisit.id);
      dispatch('changeCustomerDetailValues', [{
        field: 'virtualVisits',
        value: newVirtualVisits,
      }]);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async saveVirtualVisit({ dispatch, state, rootState }, payload) {
    const {
      virtualVisit,
    } = payload;
    const {
      virtualVisits,
      customer: {
        id: customerId,
      },
    } = state;
    const {
      admin: {
        current: {
          id: adminId,
        },
      },
    } = rootState;
    const {
      id,
      sharedUrl,
      title,
      embedUrl,
      duration,
      thumbnail_url: thumbnailUrl,
    } = virtualVisit;
    const video = {
      id,
      embedUrl,
      sharedUrl,
      title,
      adminId,
      customerId,
      duration,
      thumbnailUrl,
    };
    try {
      const { data } = await apiClient({
        method: 'POST',
        url: '/virtual/visit/save/video',
        data: video,
      });
      const newVirtualVisits = virtualVisits.slice();
      newVirtualVisits.unshift(data);
      dispatch('changeCustomerDetailValues', [{
        field: 'virtualVisits',
        value: newVirtualVisits,
      }]);
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
  async updateVirtualVist({ dispatch, state, rootState }, payload) {
    const {
      virtualVisit,
    } = payload;
    const {
      virtualVisits,
    } = state;

    const { data } = await apiClient({
      method: 'POST',
      url: '/virtual/visit/update/video',
      data: virtualVisit,
    });
    const newVirtualVisits = virtualVisits.forEach((v) => {
      if (v.id === data.id) {
        v = data;
      }
    });
    dispatch('changeCustomerDetailValues', [{
      field: 'virtalVisits',
      value: newVirtualVisits,
    }, {
      field: 'selectedVirtualVisit',
      value: data,
    }]);
  },
  async sendVirtualVisit({ dispatch, state }, notes) {
    const {
      virtualVisits,
      selectedVirtualVisit,
    } = state;

    const sendVideo = {
      ...selectedVirtualVisit,
      notes,
    };

    const { data } = await apiClient({
      method: 'POST',
      url: '/virtual/visit/send/video',
      data: sendVideo,
    });
    const newVirtualVisits = virtualVisits.map((v) => {
      let newVirtualVisit = { ...v };
      if (newVirtualVisit.id === data.id) {
        newVirtualVisit = {
          ...data,
        };
      }
      return newVirtualVisit;
    });
    dispatch('changeCustomerDetailValues', [{
      field: 'virtualVisits',
      value: newVirtualVisits,
    }]);
  },
  async getFilterBrands({ commit }) {
    const { data } = await apiClient({
      method: 'GET',
      url: 'filterbrand/all',
    });
    const filterBrands = data.map((d) => ({
      label: d.brand,
      value: d.brand,
      inSystem: d.inSystem,
    }));
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'filterBrands',
        value: filterBrands,
      }],
    });
  },
  async activateCustomer({ state, dispatch }) {
    const {
      customer,
    } = state;

    await apiClient({
      url: `/customers/id/${customer.id}/activate`,
      method: 'PUT',
    });

    const newCustomer = {
      ...customer,
      accountStatus: 'Active',
    };

    dispatch('changeCustomerDetailValue', {
      field: 'customer',
      value: newCustomer,
    });
  },
  async deactivateCustomer({ state, dispatch, rootState }, cancelReason) {
    const {
      customer,
    } = state;

    const {
      admin,
    } = rootState;

    await apiClient({
      url: `/customers/id/${customer.id}/deactivate`,
      method: 'PUT',
      data: {
        cancelReason: `${cancelReason} - ${admin?.current?.email}`,
      },
    });

    const newCustomer = {
      ...customer,
      accountStatus: 'Cancelled',
      cancelDate: moment().toISOString(),
      cancelReason: `${cancelReason} - ${admin?.current?.email}`,
    };

    dispatch('changeCustomerDetailValue', {
      field: 'customer',
      value: newCustomer,
    });
  },
  async getAllCustomerOrdersById({ commit }, payload) {
    const {
      customerId,
    } = payload;
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingOrders',
      value: true,
    });

    const { data: cOrders } = await apiClient({
      method: 'GET',
      url: `/customer/orders/${customerId}`,
    });

    const customerOrders = cOrders.map((o, index) => {
      const name = `${o.customer.firstName} ${o.customer.lastName}`;
      const nCustomer = {
        ...o.customer,
        name,
      };
      let total = 0;
      let count = 0;
      let openCounter = 0;
      let cancelledCounter = 0;
      let holdCounter = 0;
      let error = false;
      let status = '';
      let shipDate = null;
      let deliveryDate = null;
      let shippingOption = null;
      let trackingNumber = null;
      if (o.orderLine) {
        o.orderLine.forEach((oL) => {
          const {
            status: sT,
            shippingOption: sO,
            shipDate: sD,
            deliveryDate: dD,
            trackingNumber: tN,
            sku,
          } = oL;
          if (sT !== 'Ignore' && !sku.includes('SHIPPING') && !sku.includes('SMAC10PM')) {
            total += 1;
          }
          if (sT === 'Error') {
            error = true;
          } else if (sT === 'Shipped' || sT === 'Delivered' || sT === 'Returned') {
            count += 1;
          } else if (sT === 'Open') {
            openCounter += 1;
          } else if (sT === 'Do not ship' || sT === 'Cancelled') {
            cancelledCounter += 1;
          } else if (sT === 'Waiting for review') {
            holdCounter += 1;
          }
          if (sO) {
            shippingOption = sO;
          }
          if (sD) {
            shipDate = sD;
          }
          if (dD) {
            deliveryDate = dD;
          }
          if (tN) {
            trackingNumber = tN;
          }
        });
      }
      if (error) {
        status = 'Error';
      } else if (openCounter === total) {
        status = 'No items shipped';
      } else if (cancelledCounter === total) {
        status = 'Cancelled';
      } else if (holdCounter === total) {
        status = 'Review';
      } else if (count === 0) {
        status = 'Pending fulfillment';
      } else if (count < total) {
        status = 'Partially shipped';
      } else if (count === total) {
        status = 'All items shipped';
      }

      return {
        ...o,
        status,
        shippingOption,
        shipDate,
        deliveryDate,
        trackingNumber,
        customer: nCustomer,
        expanded: false,
        selected: false,
        index,
      };
    });

    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUES,
      changes: [
        {
          field: 'customerOrders',
          value: customerOrders,
        },
        {
          field: 'loadingOrders',
          value: false,
        },
      ],
    });
  },
  async reverifyHub({ state, dispatch }) {
    const {
      currentHub: {
        uId,
      },
    } = state;
    try {
      await apiClient({
        method: 'PUT',
        url: `/devices/hub/reverify/${uId}`,
      });
      dispatch('showSuccessToast', {
        message: 'Successfully sent reverify request',
      });
    } catch (error) {
      dispatch('showAlertToast', {
        message: 'There was an error reverifying this hub',
      });
    }
  },
  async removeHub({ dispatch, state }) {
    const {
      currentHub,
      activeTab,
    } = state;
    try {
      await apiClient({
        method: 'PUT',
        url: '/devices/hub/deactivate',
        data: currentHub,
      });
      dispatch('setDetailCustomer', {
        customerId: currentHub.customerId,
        activeTab,
        wasDeleted: { uid: currentHub.uId },
      });
    } catch (err) {
      dispatch('showAlertToast', {
        message: `There was an error removing ${currentHub.uId}`,
      });
      throw err;
    }
  },
  async getLatestTimepointData({ state }) {
    const {
      currentSensor,
    } = state;
    const { data } = await apiClient({
      method: 'GET',
      url: `/devices/data/timepoint/${currentSensor.serialNumber}/last`,
    });
    return data;
  },
  async removeSensor({ dispatch, state }) {
    const {
      currentSensor,
      sensors,
    } = state;
    try {
      await apiClient({
        method: 'PUT',
        url: `/devices/sensor/deactivate/sensor/${currentSensor.serialNumber}`,
      });
      const newSensors = sensors.filter((sensor) => (
        sensor.serialNumber !== currentSensor.serialNumber
      ));

      dispatch('changeCustomerDetailValue', {
        field: 'sensors',
        value: newSensors,
      });
      dispatch('showSuccessToast', {
        message: `Successfully deleted ${currentSensor.serialNumber}`,
      });
    } catch (err) {
      dispatch('showAlertToast', {
        message: `There was an error removing ${currentSensor.serialNumber}`,
      });
    }
  },
  async getPerformanceScores({ commit }, serialNumber) {
    commit({
      type: CUSTOMER_DETAIL_CHANGE_VALUE,
      field: 'loadingPerformanceScores',
      value: true,
    });
    try {
      const { data } = await apiClient({
        method: 'GET',
        url: `/measurequick/results/serial/${encodeURIComponent(serialNumber)}/by-type`,
      });
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUES,
        changes: [{
          field: 'loadingPerformanceScores',
          value: false,
        }, {
          field: 'performanceScores',
          value: data,
        }],
      });
    } catch {
      commit({
        type: CUSTOMER_DETAIL_CHANGE_VALUE,
        field: 'loadingPerformanceScores',
        value: false,
      });
    }
  },
};

export default actions;
